import { useState, useEffect, useMemo, ComponentProps } from 'react';
import { inject, observer } from 'mobx-react';

import { SelectInput, MultiSelectInput } from '../forms';
import { useComponentDidMount } from '../../hooks';
import {
  PartnerDivisionStore,
  partnerDivisionActions,
  UserColleagueItemInterface,
} from '../../../partners/store/divisions/partnerDivisionStore';
import { PartnerUserItemInterface, UserStore, userActions } from '../../../users/store/userStore';

/**
 * Опция пользователя в том формате, в котором компонент отдаёт её в селект
 * (dataValue='userId', dataTitle='userName'). Источники с другими полями
 * (`id`/`fullName`, `userId`/`userFullName`) конвертируются функциями-ремейками ниже.
 */
interface UserOption {
  userId: string,
  userName?: string,
}

// Чистые мапперы вынесены на уровень модуля, чтобы не попадать в deps useMemo
const dataColleaguesRemake = (arr: UserColleagueItemInterface[]): UserOption[] => {
  return arr.map(v => { return ({ userId: v.userId, userName: v.userFullName }); });
};

const usersRemake = (arr: PartnerUserItemInterface[]): UserOption[] => {
  return arr.map(v => { return ({ userId: v.id, userName: v.fullName }); });
};

/**
 * Выбор пользователя. Три источника данных (по приоритету):
 * 1. `partnerId` — пользователи партнёра (`userStore.usersByPartnerId[partnerId]`, поля id/fullName);
 * 2. `showColleagues` без `divisionId` — коллеги (`partnerDivisionStore.userDivisionsColleagues`);
 * 3. иначе — пользователи подразделения (`partnerDivisionStore.usersByDivision`, грузится по `divisionId`).
 *
 * `defaultValue` переобъявлен явно типом MultiSelectInput (unknown[]) — потребители передают
 * его в multi-режиме; в union ComponentProps он конфликтует с react-select-версией из SelectInput.
 * `value`, наоборот, исключён (как и в остальных селекторах): типы веток конфликтуют
 * (SelectInput ждёт string|number|null, MultiSelectInput — object|unknown[]), а реальные
 * потребители `value` не передают; в single-ветке он задаётся явно через `addUserId`.
 */
type UserSelectorProps = Omit<ComponentProps<typeof SelectInput | typeof MultiSelectInput>, 'value' | 'defaultValue' | 'data'> & {
  name?: string,
  /** Начальное значение-массив для multi-режима (прокидывается в MultiSelectInput). */
  defaultValue?: unknown[],
  /** id пользователя, которого нужно гарантированно показать в списке (например, текущее значение при редактировании). */
  addUserId?: string,
  /** Подпись для addUserId (если пользователя нет в загруженном списке). */
  addUserName?: string,
  /** Партнёр, чьи пользователи загружаются (источник 1). */
  partnerId?: string,
  /** Подразделение, пользователи которого загружаются (источник 3). */
  divisionId?: string,
  multiSelect?: boolean,
  /** Показывать коллег аутентифицированного пользователя (источник 2). */
  showColleagues?: boolean,
  /** Скрыть из списка пользователей с этими userId (кроме addUserId). */
  excludedUsers?: string[],
  /** из inject — пользователи подразделения и коллеги. */
  partnerDivisionStore?: PartnerDivisionStore,
  /** из inject — все пользователи партнёра (источник 1). */
  userStore?: UserStore,
};

const UserSelector = (props: UserSelectorProps) => {
  const {
    name = 'userId',
    partnerDivisionStore,
    userStore,
    addUserId,
    addUserName,
    divisionId,
    multiSelect = false,
    showColleagues,
    partnerId,
    excludedUsers,
    ...rest
  } = props;
  const [loading, setLoading] = useState(false);

  const data = partnerDivisionStore.usersByDivision;
  const selectedDivisionId = partnerDivisionStore.usersByDivisionId;
  const dataColleagues = partnerDivisionStore.userDivisionsColleagues;
  const loadingUserDivisionsColleagues = partnerDivisionStore.loadingUserDivisionsColleagues;
  const users = partnerId ? userStore.usersByPartnerId[partnerId] ?? [] : [];

  // Мемоизированный список пользователей из разных источников, в зависимости от параметров
  const dataArray = useMemo((): UserOption[] => {
    if (partnerId) {
      if (!addUserId) return usersRemake(users);
      if (users.find(v => v.id === addUserId)) return usersRemake(users);
      return usersRemake(users).concat([{ userId: addUserId, userName: addUserName }]);
    }
    if ((showColleagues) && (!divisionId)) {
      if (!addUserId) return dataColleaguesRemake(dataColleagues);
      if (dataColleagues.find(v => v.userId === addUserId)) return dataColleaguesRemake(dataColleagues);
      return dataColleaguesRemake(dataColleagues).concat([{ userId: addUserId, userName: addUserName }]);
    }
    if (!addUserId) return data;
    if (data.find(v => v.userId === addUserId)) return data;
    return data.concat([{ userId: addUserId, userName: addUserName }]);
  }, [addUserId, addUserName, data, dataColleagues, divisionId, partnerId, showColleagues, users]);

  const dataArrayWithoutExcluded = useMemo((): UserOption[] => {
    if (excludedUsers) {
      return dataArray.filter(v => (!excludedUsers.includes(v.userId) || (v.userId === addUserId)));
    }
    return dataArray;
  }, [addUserId, dataArray, excludedUsers]);

  useComponentDidMount(() => {
    if ((showColleagues) && (!loadingUserDivisionsColleagues) && (dataColleagues.length === 0)) {
      setLoading(true);
      // primary=false — 1:1 с бывшим Redux-вызовом (эксперимент 'false' от 01.03.2023)
      partnerDivisionActions.getAuthUserDivisionsColleagues(false).finally(() => setLoading(false));
    }
  });

  useEffect(() => {
    if ((divisionId) && (divisionId !== selectedDivisionId)) {
      setLoading(true);
      partnerDivisionActions.getUsersByDivision({ divisionId, onlyPrimary: true }).finally(() => setLoading(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [divisionId]);

  useEffect(() => {
    if (partnerId) {
      setLoading(true);
      userActions.getUsers(partnerId).finally(() => setLoading(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerId]);

  return (
    multiSelect
      ? (
        <MultiSelectInput
          { ...rest }
          name={ name }
          isMulti
          noOptionsMessage={ () => { return 'Нет вариантов'; } }
          data={ dataArrayWithoutExcluded }
          dataTitle='userName'
          dataValue='userId'
          loading={ loading }
          sort
        />
      )
      : (
        <SelectInput
          { ...rest }
          name={ name }
          data={ dataArrayWithoutExcluded }
          dataTitle='userName'
          dataValue='userId'
          loading={ loading }
          sort
          value={ addUserId }
        />
      )
  );
};

export default inject('partnerDivisionStore', 'userStore')(observer(UserSelector));
